/**
 * Tenant Server API Client
 *
 * JWT-authenticated client for ERPNext Tenant Server.
 * Unlike FrappeClient (session-based), this uses Bearer tokens.
 *
 * On 401: attempts one token refresh before logging out.
 * This is the primary client for control_plane auth mode.
 */

import { useAuthStore, ensureFreshToken } from '@/lib/store/authStore';
import { refreshCPToken } from '@/lib/api/controlPlaneClient';

/**
 * Get headers for Tenant Server requests.
 * Uses late-binding to always get fresh token.
 */
function getTenantHeaders(): HeadersInit {
  const { cpToken, currentTenant } = useAuthStore.getState();

  if (!cpToken) {
    throw new Error('Not authenticated. No JWT token available.');
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${cpToken}`,
    'X-Tenant-ID': currentTenant || '',
  };
}

/**
 * Get the Tenant Server URL from auth state.
 */
function getTenantServerUrl(): string {
  const { tenantServerUrl } = useAuthStore.getState();

  if (!tenantServerUrl) {
    throw new Error('Tenant server URL not configured. Login may have failed.');
  }

  return tenantServerUrl;
}

/**
 * Strip HTML tags from Frappe error messages.
 * Frappe uses frappe.bold() which wraps text in <strong> tags —
 * these leak into API error responses as literal markup.
 */
export function stripHtmlTags(text: string): string {
  return text.replace(/<[^>]*>/g, '');
}

/**
 * Parse Frappe error response into a human-readable message.
 */
function parseFrappeError(error: Record<string, any>): string {
  let msg: string;
  if (error._server_messages) {
    try {
      const msgs = JSON.parse(error._server_messages);
      // _server_messages is a JSON array of JSON strings — each entry is double-encoded.
      // Parse the inner JSON string to extract the human-readable .message field.
      if (typeof msgs[0] === 'string') {
        try {
          const parsed = JSON.parse(msgs[0]);
          if (parsed.message) { msg = parsed.message; return stripHtmlTags(msg); }
        } catch {
          return stripHtmlTags(msgs[0]);
        }
      }
      msg = JSON.stringify(msgs[0]);
    } catch {
      msg = String(error._server_messages);
    }
  } else if (error.message) {
    msg = error.message;
  } else if (error.exc_type) {
    msg = `${error.exc_type}: ${error.exc || 'Unknown error'}`;
  } else {
    msg = 'API Error';
  }
  return stripHtmlTags(msg);
}

/**
 * Fetch wrapper with automatic 401 → refresh → retry.
 * Attempts one token refresh on 401 before logging out.
 * Re-reads headers after refresh to pick up the new token.
 */
async function tenantFetch(
  url: string,
  init: RequestInit,
  customHeaders?: HeadersInit,
): Promise<Response> {
  // Wait for any in-flight token refresh (e.g. during rehydration) before reading the token.
  // Without this, requests fire with the expired token → 401 → noisy retry cycle.
  await ensureFreshToken();

  const headers = customHeaders || getTenantHeaders();
  let response = await fetch(url, { ...init, headers, credentials: 'omit' });

  if (response.status === 401) {
    const refreshResult = await refreshCPToken();
    if (refreshResult) {
      // Update auth store with refreshed token
      useAuthStore.setState({
        cpToken: refreshResult.access_token,
        cpTokenExpiresAt: refreshResult.expires_at,
      });
      // Retry with fresh headers (getTenantHeaders reads from updated store)
      const freshHeaders = customHeaders
        ? { ...customHeaders, 'Authorization': `Bearer ${refreshResult.access_token}` }
        : getTenantHeaders();
      response = await fetch(url, { ...init, headers: freshHeaders, credentials: 'omit' });
    }
  }

  if (!response.ok) {
    if (response.status === 401) {
      // Refresh was attempted and failed, or second request still 401
      useAuthStore.getState().logoutControlPlane();
      throw new Error('Session expired. Please log in again.');
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(parseFrappeError(error));
  }

  return response;
}

/**
 * Tenant Server Resource API
 * Mirrors FrappeClient interface for compatibility.
 */
export const tenantServer = {
  /**
   * List documents of a doctype
   */
  async list<T = any>(
    doctype: string,
    params: {
      filters?: any[] | Record<string, any>;
      or_filters?: Array<[string, string, any]>;
      fields?: string[];
      limit_start?: number;
      limit_page_length?: number;
      order_by?: string;
    } = {}
  ): Promise<T[]> {
    const url = new URL(`${getTenantServerUrl()}/api/resource/${doctype}`);

    if (params.fields) url.searchParams.set('fields', JSON.stringify(params.fields));
    if (params.filters) url.searchParams.set('filters', JSON.stringify(params.filters));
    if (params.or_filters) url.searchParams.set('or_filters', JSON.stringify(params.or_filters));
    if (params.limit_start !== undefined) url.searchParams.set('limit_start', String(params.limit_start));
    if (params.limit_page_length !== undefined) url.searchParams.set('limit_page_length', String(params.limit_page_length));
    if (params.order_by) url.searchParams.set('order_by', params.order_by);

    const response = await tenantFetch(url.toString(), { method: 'GET' });
    const data = await response.json();
    return data.data as T[];
  },

  /**
   * List documents with pagination metadata
   */
  async listWithPagination<T = any>(
    doctype: string,
    params: {
      filters?: any[] | Record<string, any>;
      or_filters?: Array<[string, string, any]>;
      fields?: string[];
      limit_start?: number;
      limit_page_length?: number;
      order_by?: string;
    } = {}
  ): Promise<{ data: T[]; total: number; pageLength: number }> {
    const data = await this.list<T>(doctype, params);

    // Get count for pagination
    let total = data.length;
    try {
      total = await this.getCount(doctype, params.filters as Record<string, any>);
    } catch {
      // Fallback to data length
    }

    return {
      data,
      total,
      pageLength: params.limit_page_length || 20,
    };
  },

  /**
   * Get a single document
   */
  async get<T = any>(doctype: string, name: string): Promise<T> {
    const url = `${getTenantServerUrl()}/api/resource/${doctype}/${encodeURIComponent(name)}`;
    const response = await tenantFetch(url, { method: 'GET' });
    const data = await response.json();
    return data.data as T;
  },

  /**
   * Create a new document
   */
  async create<T = any>(doctype: string, data: Record<string, any>): Promise<T> {
    const url = `${getTenantServerUrl()}/api/resource/${doctype}`;
    const response = await tenantFetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    const result = await response.json();
    return result.data as T;
  },

  /**
   * Update an existing document
   */
  async update<T = any>(doctype: string, name: string, data: Record<string, any>): Promise<T> {
    const url = `${getTenantServerUrl()}/api/resource/${doctype}/${encodeURIComponent(name)}`;
    const response = await tenantFetch(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    const result = await response.json();
    return result.data as T;
  },

  /**
   * Delete a document
   */
  async delete(doctype: string, name: string): Promise<void> {
    const url = `${getTenantServerUrl()}/api/resource/${doctype}/${encodeURIComponent(name)}`;
    await tenantFetch(url, { method: 'DELETE' });
  },

  /**
   * Call a Frappe method
   */
  async call<T = any>(method: string, args?: Record<string, any>): Promise<T> {
    const url = `${getTenantServerUrl()}/api/method/${method}`;
    const response = await tenantFetch(url, {
      method: 'POST',
      body: args ? JSON.stringify(args) : undefined,
    });
    const data = await response.json();
    return data.message as T;
  },

  /**
   * Call a Frappe method and return the response as a Blob.
   * Used for endpoints that return binary data (PDF, images).
   */
  async callBlob(method: string, args?: Record<string, any>): Promise<Blob> {
    const url = `${getTenantServerUrl()}/api/method/${method}`;
    const response = await tenantFetch(url, {
      method: 'POST',
      body: args ? JSON.stringify(args) : undefined,
    });
    return response.blob();
  },

  /**
   * Get count of documents
   */
  async getCount(doctype: string, filters?: Record<string, any>): Promise<number> {
    return this.call<number>('frappe.client.get_count', { doctype, filters });
  },

  /**
   * Upload a file to Tenant Server
   * Uses JWT auth instead of CSRF + session cookies
   */
  async uploadFile(
    file: File,
    options: {
      doctype?: string;
      docname?: string;
      fieldname?: string;
      isPrivate?: boolean;
      folder?: string;
    } = {}
  ): Promise<{ file_url: string; file_name: string; name: string }> {
    const baseUrl = getTenantServerUrl();
    const url = `${baseUrl}/api/method/upload_file`;

    const formData = new FormData();
    formData.append('file', file);

    if (options.isPrivate != null) {
      formData.append('is_private', options.isPrivate ? '1' : '0');
    }
    if (options.doctype) formData.append('doctype', options.doctype);
    if (options.docname) formData.append('docname', options.docname);
    if (options.fieldname) formData.append('fieldname', options.fieldname);
    if (options.folder) formData.append('folder', options.folder);

    const { cpToken, currentTenant } = useAuthStore.getState();

    // Upload uses custom headers (no Content-Type — let browser set multipart boundary)
    const uploadHeaders: HeadersInit = {
      'Authorization': `Bearer ${cpToken}`,
      'X-Tenant-ID': currentTenant || '',
    };

    const response = await tenantFetch(url, {
      method: 'POST',
      body: formData,
    }, uploadHeaders);

    const result = await response.json();
    return {
      name: result.message.name,
      file_url: result.message.file_url,
      file_name: result.message.file_name || file.name,
    };
  },

  /**
   * Call a Frappe method with FormData (for file uploads to custom endpoints).
   * Uses JWT auth instead of CSRF + session cookies.
   */
  async callWithFormData<T = any>(method: string, formData: FormData): Promise<T> {
    const baseUrl = getTenantServerUrl();
    const url = `${baseUrl}/api/method/${method}`;

    const { cpToken, currentTenant } = useAuthStore.getState();
    const uploadHeaders: HeadersInit = {
      'Authorization': `Bearer ${cpToken}`,
      'X-Tenant-ID': currentTenant || '',
    };

    const response = await tenantFetch(url, {
      method: 'POST',
      body: formData,
    }, uploadHeaders);

    const data = await response.json();
    return data.message as T;
  },
};
